import React from "react";
import {
  Grid,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  Popover,
  Button,
  DialogContent,
  DialogActions,
} from "@material-ui/core";
import styled from "styled-components";
import DateInput from "app/common/components/input/Date";

/**
 * OPERATORS
 * Applied as: [current column value] <operator> [rhs_value | rhs_column_value]
 */
const OPERATORS = [
  { label: "= (equals)", value: "=" },
  { label: "<> (not equals)", value: "<>" },
  { label: "> (greater than)", value: ">" },
  { label: "< (less than)", value: "<" },
  { label: ">= (greater than or equal)", value: ">=" },
  { label: "<= (less than or equal)", value: "<=" },
];

const RHS_TYPES = [
  { label: "Value", value: "value" },
  { label: "Column", value: "column" },
];

const DEFAULT_CRITERIA = {
  operator: ">",
  rhs_type: "value",
  rhs_value: "",
};

function DatetimeCriteriaConfig({
  anchorEl,
  onClose,
  values,
  setFieldValue,
  valuesPath = "",
  columnType,
  store,
}) {
  const open = Boolean(anchorEl);

  const [draft, setDraft] = React.useState(DEFAULT_CRITERIA);

  React.useEffect(() => {
    if (open) {
      setDraft(values.options.datetime_criteria
        ? { ...values.options.datetime_criteria }
        : { ...DEFAULT_CRITERIA }
      );
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const currentColumnId = values.column_id;

  const peerDateColumns = React.useMemo(() => {
    try {
      const allColumns = store?.getCurrentListColumns?.() ?? [];
      return allColumns.filter((col) => {
        const typeId = parseInt(col.column_type_id, 10);
        return (typeId === 4 || typeId === 16) && col.id !== currentColumnId;
      });
    } catch {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, currentColumnId, open]);

  const handleOperatorChange = (e) => {
    setDraft((prev) => ({ ...prev, operator: e.target.value }));
  };

  const handleRhsTypeChange = (e) => {
    const newType = e.target.value;
    if (newType === "value") {
      setDraft((prev) => {
        const next = { ...prev, rhs_type: "value", rhs_value: prev.rhs_value ?? "" };
        delete next.rhs_column_id;
        return next;
      });
    } else {
      setDraft((prev) => {
        const next = {
          ...prev,
          rhs_type: "column",
          rhs_column_id: peerDateColumns[0]?.id ?? "",
        };
        delete next.rhs_value;
        return next;
      });
    }
  };

  const handleRhsColumnChange = (e) => {
    setDraft((prev) => ({ ...prev, rhs_column_id: e.target.value }));
  };

  const handleRhsValueChange = (dateObj) => {
    if (!dateObj) return;
    const d = new Date(dateObj);
    const pad = (n) => String(n).padStart(2, "0");
    const formatted =
      columnType === "datetime"
        ? `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`
        : `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    setDraft((prev) => ({ ...prev, rhs_value: formatted }));
  };

  const handleOk = () => {
    setFieldValue(`${valuesPath}options.datetime_criteria`, { ...draft });
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  const handleRemove = () => {
    setFieldValue(`${valuesPath}options.datetime_criteria`, null);
    onClose();
  };

  const hasExistingCriteria = !!values.options.datetime_criteria;

  return (
    <StyledPopover
      open={open}
      anchorEl={anchorEl}
      onClose={handleCancel}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      transformOrigin={{ vertical: "top", horizontal: "left" }}
    >
      <StyledDialogContent>
        <Grid container direction="column" spacing={2}>
          <Grid item xs={12}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Operator</InputLabel>
              <Select
                label="Operator"
                value={draft.operator ?? ">"}
                onChange={handleOperatorChange}
              >
                {OPERATORS.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {op.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>Compare with</InputLabel>
              <Select
                label="Compare with"
                value={draft.rhs_type ?? "value"}
                onChange={handleRhsTypeChange}
              >
                {RHS_TYPES.map((rt) => (
                  <MenuItem key={rt.value} value={rt.value}>
                    {rt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            {draft.rhs_type === "column" ? (
              peerDateColumns.length === 0 ? (
                <ErrorText variant="caption">
                  No other date / datetime columns found in this sheet.
                </ErrorText>
              ) : (
                <FormControl variant="outlined" size="small" fullWidth>
                  <InputLabel>Select column</InputLabel>
                  <Select
                    label="Select column"
                    value={draft.rhs_column_id ?? ""}
                    onChange={handleRhsColumnChange}
                  >
                    {peerDateColumns.map((col) => (
                      <MenuItem key={col.id} value={col.id}>
                        {col.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )
            ) : (
              <DateInput
                value={draft.rhs_value || null}
                isDateTime={columnType === "datetime"}
                underLine={true}
                styledPopper={false}
                onChange={handleRhsValueChange}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <PreviewBox>
              <code>
                {buildPreviewExpression(draft, peerDateColumns, columnType)}
              </code>
            </PreviewBox>
          </Grid>
        </Grid>
      </StyledDialogContent>

      <DialogActions style={{ justifyContent: "space-between" }}>
        <div>
          {hasExistingCriteria && (
            <RemoveButton onClick={handleRemove}>
              Remove Criteria
            </RemoveButton>
          )}
        </div>
        <div>
          <Button onClick={handleCancel} color="primary">
            Cancel
          </Button>
          <Button variant="contained" onClick={handleOk} color="primary">
            OK
          </Button>
        </div>
      </DialogActions>
    </StyledPopover>
  );
}

function buildPreviewExpression(draft, peerDateColumns, columnType) {
  if (!draft) return "";
  const { operator, rhs_type, rhs_value, rhs_column_id } = draft;
  const lhs = "[this column]";
  const op = operator || "?";

  if (rhs_type === "column") {
    if (!rhs_column_id) return `${lhs} ${op} [select a column]`;
    const col = peerDateColumns.find((c) => c.id === rhs_column_id);
    const colLabel = col
      ? `column_${col.id}  (${col.name})`
      : `column_${rhs_column_id}`;
    return `${lhs} ${op} ${colLabel}`;
  }

  if (!rhs_value) {
    return `${lhs} ${op} [enter a ${
      columnType === "datetime" ? "date & time" : "date"
    } value]`;
  }
  return `${lhs} ${op} '${rhs_value}'`;
}

const StyledPopover = styled(({ className, ...props }) => (
  <Popover {...props} classes={{ paper: className }} />
))`
  && {
    width: 340px;
  }
`;

const StyledDialogContent = styled(DialogContent)`
  min-width: 300px;
`;

const ErrorText = styled(Typography)`
  color: #c0392b !important;
  font-size: 11px !important;
`;

const PreviewBox = styled.div`
  background: #f5f7fa;
  border: 1px solid #e0e4ea;
  border-radius: 4px;
  padding: 6px 10px;
  font-size: 11px;
  color: #333;
  word-break: break-all;
  min-height: 28px;
`;

const RemoveButton = styled(Button)`
  color: #c0392b !important;
  font-size: 12px !important;
  text-transform: none !important;
  padding: 4px 8px !important;
  &:hover {
    background: rgba(192, 57, 43, 0.05) !important;
  }
`;

export default DatetimeCriteriaConfig;
